import type { ActionContext, Module } from "vuex";
import baseAPI from "@/api/base";
import { GET_USER_URL } from "@/utils/urls";
import { BOOTSTRAP_IMPERSONATION } from "@/store/action-types";
import {
  SET_IMPERSONATION,
  CLEAR_IMPERSONATION,
  SET_ACCOUNT,
  SET_SESSION_TOKEN,
} from "@/store/mutation-types";
import { parseImpersonationToken } from "@/utils/parseImpersonationToken";

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedBy: string | null;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
}

const impersonation: Module<ImpersonationState, {}> = {
  state: (): ImpersonationState => ({
    isImpersonating: false,
    impersonatedBy: null,
    userId: null,
    userName: null,
    userEmail: null,
  }),
  getters: {
    isImpersonating: (state) => state.isImpersonating,
    impersonationInfo: (state) => ({
      impersonatedBy: state.impersonatedBy,
      userId: state.userId,
      userName: state.userName,
      userEmail: state.userEmail,
    }),
  },
  mutations: {
    [SET_IMPERSONATION]: (state, payload: Partial<ImpersonationState>) => {
      state.isImpersonating = payload.isImpersonating ?? true;
      if (payload.impersonatedBy !== undefined) state.impersonatedBy = payload.impersonatedBy;
      if (payload.userId !== undefined) state.userId = payload.userId;
      if (payload.userName !== undefined) state.userName = payload.userName;
      if (payload.userEmail !== undefined) state.userEmail = payload.userEmail;
    },
    [CLEAR_IMPERSONATION]: (state) => {
      state.isImpersonating = false;
      state.impersonatedBy = null;
      state.userId = null;
      state.userName = null;
      state.userEmail = null;
    },
  },
  actions: {
    [BOOTSTRAP_IMPERSONATION]: async (
      { commit }: ActionContext<ImpersonationState, {}>,
      token: string
    ) => {
      try {
        const payload = parseImpersonationToken(token);
        if (!payload) throw new Error("Invalid impersonation token");

        // Set session token first so the request interceptor uses it
        commit(SET_SESSION_TOKEN, token);
        commit(SET_IMPERSONATION, {
          isImpersonating: true,
          impersonatedBy: payload.impersonatedBy,
          userId: payload.id,
        });

        // Fetch the impersonated user's full profile
        const result = await baseAPI.get(GET_USER_URL, { params: { id: payload.id } });
        const userData = result.data?.data?.[0];
        if (userData) {
          commit(SET_IMPERSONATION, {
            isImpersonating: true,
            impersonatedBy: payload.impersonatedBy,
            userId: userData.id,
            userName: userData.fullName,
            userEmail: userData.email,
          });
          commit(SET_ACCOUNT, { data: userData, isLoading: false });
        }
      } catch (e) {
        console.error("[Impersonation] Bootstrap failed:", e);
        throw e;
      }
    },
  },
};

export default impersonation;
